<template>
  <v-app-bar color="surface" :order="0">
    <div class="d-flex flex-grow-1 ga-4 h-100 align-center">
      <img
        v-if="selectedCompanyId"
        class="hidden-sm-and-down flex-shrink-0"
        style="cursor:pointer"
        id="logo-src"
        @click="goTo('Home')"
        v-bind:src="logoData"
        alt="Company logo"
      />

      <v-autocomplete
        v-model="selectedItem"
        v-model:menu="menuOpen"
        :items="searchMatches"
        item-title="title"
        item-value="id"
        hide-details
        variant="outlined"
        density="compact"
        auto-select-first
        color="info"
        prepend-inner-icon="mdi-magnify"
        :loading="loading"
        v-model:search="searchQuery"
        placeholder="Search all documents..."
        persistent-placeholder
        :custom-filter="() => true"
        return-object
        @update:modelValue="onSelect"
        class="flex-grow-1"
        style="max-width: 60%"
        @blur="onBlur"
      >
        <template #item="{ item, props }">
          <v-list-item v-bind="props">
            <template #prepend>
              <v-icon v-if="item.raw.isSearch">mdi-magnify</v-icon>
              <v-icon v-else>mdi-file-document</v-icon>
            </template>
            <v-list-item-subtitle v-if="item.raw.matchText" class="match-text text-truncate">
              <span v-html="item.raw.matchText"></span>
            </v-list-item-subtitle>
          </v-list-item>
        </template>
      </v-autocomplete>
    </div>

    <div class="d-flex ga-4 h-100 align-center">
      <v-btn variant="text" @click="goTo('ConnectionsList')" class="text-none" stacked>
        <v-badge :content="availableConnectionsCount" color="error" :model-value="availableConnectionsCount > 0">
          <v-icon size="24">mdi-domain</v-icon>
        </v-badge>
        <span>Connections</span>
      </v-btn>

      <h3 class="text-h6" v-if="getCompanyName()">{{ getCompanyName() }}</h3>

      <v-menu>
        <template v-slot:activator="{ props }">
          <div>
            <v-btn icon size="large" v-bind="props" v-if="!$auth0.isAuthenticated.value && !$auth0.isLoading.value">
              <v-icon>mdi-account-circle</v-icon>
            </v-btn>
            <v-avatar
              v-bind="props"
              v-if="$auth0.isAuthenticated.value && !$auth0.isLoading.value"
              data-cy="profile"
              color="primary"
              v-bind:class="{ 'global-user': isGlobalUser }"
              size="36"
            >
              <v-img :src="$auth0.user.value.picture"></v-img>
            </v-avatar>
          </div>
        </template>
        <v-list>
          <v-list-item
            @click="showChangeCompanyDialog = true"
            v-if="$auth0.isAuthenticated.value"
            prepend-icon="mdi-swap-horizontal"
            title="Change Account"
          >
          </v-list-item>
          <v-list-item
            @click="goTo('Notifications')"
            v-if="$auth0.isAuthenticated.value"
            prepend-icon="mdi-bell-outline"
          >
            <v-list-item-title>Notifications({{ unreadNotifications }})</v-list-item-title>
          </v-list-item>
          <v-list-item
            @click="goTo('Profile')"
            v-if="$auth0.isAuthenticated.value"
            v-show="!isGlobalUser"
            prepend-icon="mdi-account-details"
            title="Edit my Profile"
          >
          </v-list-item>
          <v-list-item
            data-cy="logout"
            @click="logout"
            v-if="$auth0.isAuthenticated.value"
            prepend-icon="mdi-logout"
            title="Logout"
          >
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <ChangeCompanyDialog
      :show="showChangeCompanyDialog"
      :cancel="() => (showChangeCompanyDialog = false)"
    ></ChangeCompanyDialog>
  </v-app-bar>
</template>

<script>
import { mapGetters } from "vuex";
import signOutService from "@/services/signOutService.js";
import filterService from "@/services/filterService.js";
import partyService from "@/services/partyService.js";
import typeaheadService from "@/services/typeaheadService";
import ChangeCompanyDialog from "@/dialogs/ChangeCompanyDialog.vue";

export default {
  props: {},

  components: {
    ChangeCompanyDialog,
  },

  data() {
    return {
      searchMatches: [],
      availableConnections: 0,
      searchQuery: "",
      selectedItem: null,
      menuOpen: false,
      highlight: filterService.highlight,
      loading: false,
      showChangeCompanyDialog: false,
    };
  },

  watch: {
    async searchQuery(query) {
      if (!query) {
        this.searchMatches = [];
        this.menuOpen = false;
        return;
      }

      this.loading = true;
      let typeaheadResults = await typeaheadService.search(query);
      this.searchMatches = [{ title: `Search for "${query}"`, isSearch: true }, ...typeaheadResults];
      this.menuOpen = true;

      this.loading = false;

      return query;
    },
    $route() {
      // Reset search when route changes
      this.resetSearch();
    },
  },

  methods: {
    goTo(route) {
      this.$router.push({ name: route });
    },
    getAvailableConnections() {
      partyService.getPartyNetworkMatches().then(parties => {
        this.availableConnections = parties;
        this.$store.dispatch("invitationStore/setAvailableConnectionsCount", parties);
      });
    },
    getCompanyName() {
      var company = this.companies.find(x => x.id == this.selectedCompanyId);

      if (company) {
        return company.name;
      }
    },
    async logout() {
      await signOutService.signOut();
    },
    onSelect(item) {
      if (!item) return;

      // Close menu immediately
      this.menuOpen = false;

      if (item.isSearch) {
        this.onSearch();
        this.resetSearch();
        return;
      }

      // Store document ID and reset search state first
      const documentId = item.id;
      this.resetSearch();

      // Navigate after resetting state
      this.$nextTick(() => {
        this.$router.push({ name: "Document", params: { id: `${documentId}` } });
      });
    },
    onBlur() {
      // Delay to allow click event to fire first
      setTimeout(() => {
        if (!this.selectedItem) {
          this.resetSearch();
        }
      }, 200);
    },
    resetSearch() {
      this.menuOpen = false;
      this.searchMatches = [];
      this.searchQuery = "";
      this.selectedItem = null;
    },
    onSearch() {
      this.searchMatches = [];
      this.$router.push(`/search?q=${encodeURIComponent(this.searchQuery)}`);
    },
  },
  async created() {
    this.getAvailableConnections();
  },

  computed: {
    adminOperations() {
      return [{ icon: "domain", title: "Connections", to: "/admin/connections" }];
    },
    ...mapGetters("auth0Store", ["companies", "isGlobalUser", "selectedCompanyId"]),
    ...mapGetters("logoStore", ["logoData"]),
    ...mapGetters("userProfile", ["unreadNotifications"]),
    ...mapGetters("invitationStore", ["availableConnectionsCount"]),
  },
};
</script>

<style scoped>
.v-app-bar :deep(.v-toolbar__content) {
  height: 100%;
  justify-content: space-between;
  padding: 4px 16px;
}

:deep(.match-text em) {
  background-color: rgb(var(--v-theme-primary), 0.12);
  color: rgb(var(--v-theme-primary));
  font-style: normal;
  font-weight: 600;
  padding: 1px 2px;
}

:deep(.v-toolbar__content) {
  gap: 32px;
}
</style>
